import type { GridColDef, GridValidRowModel } from '@mui/x-data-grid-pro';
import type { IntlShape } from 'react-intl';
import { formatTranslation, type TranslationKey } from 'intl';
import { FiltersTranslationKeys } from 'intl/keys';
import { CustomFilterOperator } from '../columnsFactory/customFilters/operators/constants';
import { GridFilterValue } from './operators';

/**
 * "Is not" variants of the filter operators (PT-7187): an operator that
 * selects values or a date range gets a negated twin matching what it does
 * not match. Its value is the selecting operator's value with a prefix, so a
 * saved filter row says which operator it negates.
 */
const NEGATED_OPERATOR_PREFIX = 'not:';

/** The label of each negatable operator's negation. */
const NEGATION_LABEL_OF_OPERATOR: Readonly<Record<string, TranslationKey>> = {
  [CustomFilterOperator.CONTAINS]: FiltersTranslationKeys.operatorIsNot,
  [GridFilterValue.IS_ANY_OF]: FiltersTranslationKeys.operatorIsNot,
  [CustomFilterOperator.DATE_RANGE]: FiltersTranslationKeys.operatorNotInDateRange,
};

/** The operator of a filter row and whether its condition is negated. */
export interface FilterRowOperator {
  operator: string;
  negated: boolean;
}

export function toNegatedOperatorValue(operatorValue: string): string {
  return `${NEGATED_OPERATOR_PREFIX}${operatorValue}`;
}

/** Splits a filter row's operator into the operator it applies and the negation. */
export function readFilterRowOperator(operatorValue: string): FilterRowOperator {
  return operatorValue.startsWith(NEGATED_OPERATOR_PREFIX)
    ? { operator: operatorValue.slice(NEGATED_OPERATOR_PREFIX.length), negated: true }
    : { operator: operatorValue, negated: false };
}

/** The filter operators of a column, as MUI types them. */
type ColumnFilterOperators<TRow extends GridValidRowModel> = NonNullable<GridColDef<TRow>['filterOperators']>;
type ColumnFilterOperator<TRow extends GridValidRowModel> = ColumnFilterOperators<TRow>[number];

function negateFilterOperator<TRow extends GridValidRowModel>(
  operator: ColumnFilterOperator<TRow>,
  negationLabel: TranslationKey,
  intl: IntlShape
): ColumnFilterOperator<TRow> {
  return {
    ...operator,
    label: formatTranslation(intl, negationLabel),
    value: toNegatedOperatorValue(operator.value),
    getApplyFilterFn: (filterItem, column) => {
      const matches = operator.getApplyFilterFn({ ...filterItem, operator: operator.value }, column);
      return matches ? (...matchArguments) => !matches(...matchArguments) : null;
    },
  };
}

/** Columns whose operators negate themselves already (text: "does not contain", "does not equal"). */
function hasOwnNegations<TRow extends GridValidRowModel>(operators: ColumnFilterOperators<TRow>): boolean {
  return operators.some(({ value }) => value === GridFilterValue.NOT_CONTAINS);
}

/** The column's operators, each negatable one followed by its "is not" variant. */
export function withNegatedFilterOperators<TRow extends GridValidRowModel>(
  operators: ColumnFilterOperators<TRow>,
  intl: IntlShape
): ColumnFilterOperators<TRow> {
  if (hasOwnNegations(operators)) {
    return operators;
  }
  return operators.flatMap((operator) => {
    const negationLabel = NEGATION_LABEL_OF_OPERATOR[operator.value];
    return negationLabel ? [operator, negateFilterOperator(operator, negationLabel, intl)] : [operator];
  });
}
